import { httpGet, httpPost, HTTPDestinationType, httpPut, httpDelete } from '@/app/lib/adapters/httpAdapter'
import { getEnvVariable } from '@/app/service/getenvService'
import { SuggestionTypeCd } from '@/app/service/models/zipcodesuggestions'
import { ProspectiveAgentsResponse } from '@agent360/models/prospectiveagent'
import { RecruitingActivityResponse } from '@agent360/models/prospectiveagentactivity'
import { AddAgentToPipelineRequest } from '@agent360/models/addAgentToPipeline'
import { PipelineStagesResponse } from '@agent360/models/pipelinestages'
import { RecrProspects, RecrProspectsCounts } from '@agent360/models/recrprospects'
import { OfficeDetailsResponse } from '@/app/service/models/officedetails'
import { BrokeragePeopleResponse, BrokeragePersonDetailsResponse } from '@agent360/models/recruitersdetails'
import { UpdateProspectRequest } from '@agent360/models/updateprospectrequest'
import { assertApiErrorOrThrow } from '@/app/lib/errors/apiError'
import { PipelineStageResoansResponse } from '@agent360/models/nurturereasons'
import { IncentiveEstimatorData } from '@agent360/models/incentiveestimator'
import { ProspectiveAgentProfileListingResponse } from '@agent360/models/prospectiveagentprofile'

export async function getProspectiveAgents(
	queryParams: Record<string, string | number | boolean | undefined>,
	token?: string
): Promise<ProspectiveAgentsResponse> {
	let response
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/prospectiveagents`, {
			query: queryParams,
			token,
		})
		return response as ProspectiveAgentsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Prospective Agents')
	}
}

export async function getProspectiveAgent(
	partyId: string,
	agentId: string,
	mlsCd?: string,
	token?: string,
	mlsAgentId?: string
) {
	let response

	try {
		const queryParams: Record<string, string> = {
			partyId,
		}

		if (mlsCd) {
			queryParams.mlsCd = mlsCd
		}
		if (mlsAgentId) {
			queryParams.mlsAgentId = mlsAgentId
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')

		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/prospectiveagents/${agentId}`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Prospective Agent')
	}
}

export async function getProspectiveAgentActivity(
	partyId: string,
	fromDt: string,
	agentId: string,
	mlsCd?: string,
	token?: string
) {
	let response

	try {
		const queryParams: Record<string, string> = {
			partyId,
			agentId,
			fromDt,
		}

		if (mlsCd) {
			queryParams.mlsCd = mlsCd
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/prospectiveagentactivities`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Prospective Agent Activity')
	}
}

export async function getMLSListings(queryParams: Record<string, string | number>, token?: string) {
	let response
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/mlslistings`, {
			query: queryParams,
			token,
		})
		return response as ProspectiveAgentProfileListingResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET MLS Listings')
	}
}

export async function getProspectiveAgentOffices(partyId: string, agentId: string, mlsCd?: string, token?: string) {
	let response

	try {
		const queryParams: Record<string, string> = {
			partyId,
			agentId,
		}

		if (mlsCd) {
			queryParams.mlsCd = mlsCd
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')

		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/prospectiveagentoffices`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Prospective Agent Offices')
	}
}

export async function getProspectiveAgentBrokerageHistory(
	companyId: string,
	agentId: string,
	mlsCd?: string,
	token?: string
) {
	try {
		const queryParams: Record<string, string> = {
			companyId,
			agentId,
		}

		if (mlsCd) {
			queryParams.mlsCd = mlsCd
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')

		const response = await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/prospectiveagents/brokeragehistory`,
			{ query: queryParams, token }
		)

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Prospective Agent Brokerage History')
	}
}

export async function getZipCodeSuggestions(
	query: string,
	partyId: string,
	options: {
		mlsIds?: string
		queryTypeCd?: string
		token?: string
	} = {}
) {
	const { mlsIds, queryTypeCd = SuggestionTypeCd.PRODUCTIONAREA, token } = options

	try {
		const queryParams: Record<string, string> = {
			q: query,
			queryTypeCd,
			partyId,
		}

		if (mlsIds) {
			queryParams.mlsIds = mlsIds
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/recrsearchsuggestions`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Zip Code Suggestions')
	}
}

export async function getBrokerageNameSuggestions(
	query: string,
	partyId: string,
	options: {
		mlsIds?: string
		queryTypeCd?: string
		token?: string
	} = {}
) {
	const { mlsIds, queryTypeCd = SuggestionTypeCd.BROKERAGENAME, token } = options

	try {
		const queryParams: Record<string, string> = {
			q: query,
			queryTypeCd,
			partyId,
		}

		if (mlsIds) {
			queryParams.mlsIds = mlsIds
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/recrsearchsuggestions`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Brokerage Name Suggestions')
	}
}

export async function getMLSSelectSuggestions(
	partyId: string,
	options: {
		token?: string
	} = {}
) {
	const { token } = options
	try {
		const queryParams: Record<string, string> = {
			partyId,
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/mlssystems`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET MLS Select Suggestions')
	}
}

export async function getMLSBrandSelectSuggestions(
	companyId: string,
	options: {
		token?: string
	} = {}
) {
	const { token } = options
	try {
		const queryParams: Record<string, string> = {
			companyId,
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/prospectiveagentbrands`, {
			query: queryParams,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET MLS BrandSelect Suggestions')
	}
}

export async function fetchRecruitingActivityData(
	prospectId: string,
	token: string | undefined,
	companyId: string
): Promise<RecruitingActivityResponse> {
	const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
	const url = `${apiBaseUrl}/api/recrprospects/${prospectId}/activities`
	let response = await httpGet(HTTPDestinationType.EXTERNAL, url, {
		query: {
			companyId,
		},
		token,
	})
	const updatedActivities = ((response as RecruitingActivityResponse).activities ?? []).map(activity => {
		if (!activity.assigneeId || !activity.assigneeNm) {
			activity.assigneeId = activity.doerId || ''
			activity.assigneeNm = activity.doerNm || ''
		}
		return activity
	})

	const typedResponse = {
		...(response as RecruitingActivityResponse),
		activities: updatedActivities,
	}

	response = {
		...typedResponse,
		paginationData: {
			page: 0,
			pageSize: 10,
			totalItemCt: typedResponse.activities.length,
		},
	}
	return response as RecruitingActivityResponse
}

export async function logRecruitingActivity(payload: Record<string, unknown>, prospectId?: string, token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrprospects/${prospectId}/activities`
		const response = await httpPost(HTTPDestinationType.EXTERNAL, url, {
			body: payload,
			token,
		})
		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error logging recruiting activity')
	}
}

export async function editRecruitingActivity(
	activityId: string,
	payload: Record<string, unknown>,
	prospectId?: string,
	token?: string
) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrprospects/${prospectId}/activities/${activityId}`
		const response = await httpPut(HTTPDestinationType.EXTERNAL, url, {
			body: payload,
			token,
		})
		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error editing recruiting activity')
	}
}

export async function deleteRecruitingActivity(
	activityId: string,
	companyId: string,
	prospectId?: string,
	token?: string
) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrprospects/${prospectId}/activities/${activityId}`
		const response = await httpDelete(HTTPDestinationType.EXTERNAL, url, {
			query: { companyId },
			token,
		})
		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error deleting recruiting activity')
	}
}

export async function addProspectToPipeline(payload: AddAgentToPipelineRequest, token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrprospects`
		const response = await httpPost(HTTPDestinationType.EXTERNAL, url, {
			body: payload,
			token,
		})
		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error adding prospect to pipeline')
	}
}

export async function updateRecruitingProspect(
	prospectId: string,
	companyId: string,
	requestBody: UpdateProspectRequest,
	token?: string
) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrprospects/${prospectId}`

		const response = await httpPut(HTTPDestinationType.EXTERNAL, url, {
			query: { companyId },
			body: requestBody,
			token,
		})

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error updating recruiting prospect')
	}
}

export async function getRecrProspects(
	companyId: string,
	options: {
		pipelineTypeCds: string
		stageCds: string
		recruiterId?: string | null
		sortCd?: string
		includeNoActivityFl?: boolean
		lastActivityToTs?: string
		lastActivityFromTs?: string
		nextActivityToTs?: string
		nextActivityFromTs?: string
		includeOverdueToDosFl?: boolean
		includeNoUpcomingActivityFl?: boolean
		brokerageNm?: string
	},
	token?: string
): Promise<RecrProspects> {
	try {
		const { stageCds, pipelineTypeCds, recruiterId, sortCd } = options
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrprospects`

		const query: Record<string, string | boolean> = {
			companyId,
			pipelineTypeCds,
			stageCds,
		}

		if (recruiterId && recruiterId.trim() !== '') {
			query.recruiterId = recruiterId
		}

		if (sortCd && sortCd.trim() !== '') {
			query.sortCd = sortCd
		}

		if (options.includeNoActivityFl !== undefined) query.includeNoActivityFl = options.includeNoActivityFl
		if (options.lastActivityToTs) query.lastActivityToTs = options.lastActivityToTs
		if (options.lastActivityFromTs) query.lastActivityFromTs = options.lastActivityFromTs
		if (options.nextActivityToTs) query.nextActivityToTs = options.nextActivityToTs
		if (options.nextActivityFromTs) query.nextActivityFromTs = options.nextActivityFromTs
		if (options.includeOverdueToDosFl !== undefined) query.includeOverdueToDosFl = options.includeOverdueToDosFl
		if (options.includeNoUpcomingActivityFl !== undefined)
			query.includeNoUpcomingActivityFl = options.includeNoUpcomingActivityFl
		if (options.brokerageNm) query.brokerageNm = options.brokerageNm

		const response = await httpGet(HTTPDestinationType.EXTERNAL, url, {
			query,
			token,
		})

		return response as RecrProspects
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error fetching recruiting prospects')
	}
}

export async function getRecrProspectsCounts(
	companyId: string,
	options: {
		recruiterId?: string | null
	},
	token?: string,
	mocked?: boolean
): Promise<RecrProspectsCounts> {
	if (mocked) {
		return {
			pipelineCounts: [
				{
					stageCd: 'NURT',
					prospectCt: 290,
					pipelineTypeCd: 'Passive',
				},
				{
					stageCd: 'IDEN',
					prospectCt: 0,
					pipelineTypeCd: 'Active',
				},
				{
					stageCd: 'CNCT',
					prospectCt: 0,
					pipelineTypeCd: 'Active',
				},
				{
					stageCd: 'MEET',
					prospectCt: 0,
					pipelineTypeCd: 'Active',
				},
				{
					stageCd: 'NEGO',
					prospectCt: 190,
					pipelineTypeCd: 'Active',
				},
			],
		}
	}
	try {
		const { recruiterId } = options
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/recrpipelinecounts`

		const query: Record<string, string> = {
			companyId,
			recruiterREPId: recruiterId ?? '',
		}

		const response = await httpGet(HTTPDestinationType.EXTERNAL, url, {
			query,
			token,
		})

		return response as RecrProspectsCounts
	} catch (error: unknown) {
		if (error instanceof Error) {
			throw new Error(error.message)
		}
		throw new Error('An unknown error occurred')
	}
}

export async function getPipelineStages(partyId: string, token?: string): Promise<PipelineStagesResponse> {
	try {
		const queryParams: Record<string, string> = {
			partyId,
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/recrpipelinestages`, {
			query: queryParams,
			token,
		})

		return response as PipelineStagesResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Pipeline Stages')
	}
}

export async function getOffices(companyId: string, token?: string): Promise<OfficeDetailsResponse> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/offices`, {
			query: { companyId: companyId.toLocaleUpperCase() },
			token,
		})

		return response as OfficeDetailsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Offices')
	}
}

export async function getBrokeragePeople(
	companyId: string,
	token?: string,
	params?: Record<string, unknown>
): Promise<BrokeragePeopleResponse> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/brokeragepeople`, {
			query: { companyId, ...params },
			token,
		})

		return response as BrokeragePeopleResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Brokerage People')
	}
}

export async function getPipelineStageReasons(
	companyId: string,
	token?: string
): Promise<PipelineStageResoansResponse> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/recrpipelinestagereasons`, {
			query: { companyId },
			token,
		})

		return response as PipelineStageResoansResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Brokerage People')
	}
}

export interface OfficeListQueryParams {
	companyId: string
}

export async function getCompanyOffices(
	queryParams: OfficeListQueryParams,
	token?: string
): Promise<OfficeDetailsResponse> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/offices`, {
			query: queryParams,
			token,
		})
		return response as OfficeDetailsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Company Offices')
	}
}

export async function getbrokeragePersonDetailsbyRepId(companyId: string, repId: string, token?: string) {
	try {
		const queryParams: Record<string, string> = {
			companyId,
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')

		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/brokeragepeople/${repId}`, {
			query: queryParams,
			token,
		})

		return response as BrokeragePersonDetailsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Prospective Agent Brokerage History')
	}
}

export async function getIncentiveRatios(companyId: string, token?: string) {
	try {
		const queryParams: Record<string, string> = {
			companyId,
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/recrincentiveratios`, {
			query: queryParams,
			token,
		})

		return response as IncentiveEstimatorData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Incentive Ratios')
	}
}
